import React, { useCallback, useEffect, useRef, useState } from "react";
import { MarketAnalysisService } from "../../Api/MarketAnalysisService";
import type {
  MarketAnalysisData,
  MarketQuote,
  CompanyInfo,
  NewsItem,
  Analysis,
  HistoricalCandle,
} from "./types";
import "./MarketAnalysis.css";

const STOCK_OPTIONS = [
  "AAPL",
  "MSFT",
  "GOOGL",
  "AMZN",
  "META",
  "NVDA",
  "TSLA",
  "JPM",
  "V",
  "JNJ",
];

function formatTime(ms: number): string {
  const sec = Math.floor(ms / 1000);
  const min = Math.floor(sec / 60);
  if (min > 0) {
    return `${min}m ${sec % 60}s`;
  }
  return `${sec}s`;
}

/** Format ISO string as local date (day, month, year) and optional local time */
function formatDate(iso: string, options?: { includeTime?: boolean }): string {
  try {
    const d = new Date(iso);
    const datePart = d.toLocaleDateString(undefined, {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
    if (options?.includeTime) {
      const timePart = d.toLocaleTimeString(undefined, {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
      return `${datePart} at ${timePart}`;
    }
    return datePart;
  } catch {
    return iso;
  }
}

function formatNumber(n: number, decimals = 2): string {
  return n.toLocaleString(undefined, { maximumFractionDigits: decimals });
}

const MarketAnalysisPage: React.FC = () => {
  const [symbol, setSymbol] = useState("");
  const [data, setData] = useState<MarketAnalysisData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchAnalysis = useCallback(async () => {
    const s = symbol.trim().toUpperCase();
    if (!s) return;
    setError(null);
    setData(null);
    setLoading(true);
    setElapsedMs(0);
    const start = Date.now();

    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setElapsedMs(Date.now() - start);
    }, 100);

    try {
      const res = await MarketAnalysisService.getMarketAnalysis(s);
      setData(res.data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load analysis");
    } finally {
      setLoading(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setElapsedMs(Date.now() - start);
    }
  }, [symbol]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") fetchAnalysis();
  };

  return (
    <div className="market-analysis-page">
      <section className="market-analysis-hero">
        <h1 className="market-analysis-title">Market Analysis</h1>
        <p className="market-analysis-subtitle">
          Enter a stock symbol to get quote, company info, news, and AI analysis.
        </p>

        <div className="market-analysis-search">
          <input
            type="text"
            list="stock-symbols"
            placeholder="e.g. AAPL, MSFT"
            value={symbol}
            onChange={(e) => setSymbol(e.target.value)}
            onKeyDown={onKeyDown}
            className="market-analysis-input"
            disabled={loading}
            autoCapitalize="characters"
            autoComplete="off"
          />
          <datalist id="stock-symbols">
            {STOCK_OPTIONS.map((opt) => (
              <option key={opt} value={opt} />
            ))}
          </datalist>
          <div className="market-analysis-options">
            <span className="market-analysis-options-label">Quick select:</span>
            {STOCK_OPTIONS.map((opt) => (
              <button
                key={opt}
                type="button"
                className="market-analysis-chip"
                onClick={() => {
                  setSymbol(opt);
                  setError(null);
                }}
                disabled={loading}
              >
                {opt}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="market-analysis-submit"
            onClick={fetchAnalysis}
            disabled={loading || !symbol.trim()}
          >
            {loading ? "Loading…" : "Analyze"}
          </button>
        </div>

        {loading && (
          <div className="market-analysis-loading">
            <div className="market-analysis-spinner" />
            <p className="market-analysis-loading-text">Fetching analysis…</p>
            <p className="market-analysis-time">Elapsed: {formatTime(elapsedMs)}</p>
          </div>
        )}

        {error && (
          <div className="market-analysis-error" role="alert">
            {error}
          </div>
        )}
      </section>

      {data && !loading && (
        <div className="market-analysis-results">
          <QuoteCard quote={data.quote} companyInfo={data.companyInfo} />
          <AnalysisCard analysis={data.analysis} />
          <NewsCard news={data.news} />
          <HistoricalCard historical={data.historical} />
        </div>
      )}
    </div>
  );
};

const QuoteCard: React.FC<{
  quote: MarketQuote;
  companyInfo: CompanyInfo;
}> = ({ quote, companyInfo }) => {
  const isPositive = quote.change >= 0;
  return (
    <div className="market-analysis-card">
      <div className="market-analysis-card-header">
        <div className="market-analysis-quote-title">
          {companyInfo.logo ? (
            <img
              src={companyInfo.logo}
              alt=""
              className="market-analysis-logo"
            />
          ) : null}
          <div>
            <h2>{quote.symbol}</h2>
            <p className="market-analysis-meta">
              {companyInfo.sector} · {companyInfo.industry}
              {companyInfo.website ? (
                <>
                  {" · "}
                  <a
                    href={companyInfo.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="market-analysis-link"
                  >
                    Website
                  </a>
                </>
              ) : null}
            </p>
          </div>
        </div>
        <p className="market-analysis-timestamp">
          As of {formatDate(quote.timestamp, { includeTime: true })}
        </p>
      </div>
      <div className="market-analysis-quote-grid">
        <div className="market-analysis-quote-item">
          <span className="market-analysis-quote-label">Price</span>
          <span className="market-analysis-quote-value">
            ${formatNumber(quote.price)}
          </span>
        </div>
        <div className="market-analysis-quote-item">
          <span className="market-analysis-quote-label">Change</span>
          <span
            className={`market-analysis-quote-value ${
              isPositive ? "positive" : "negative"
            }`}
          >
            {isPositive ? "+" : ""}
            ${formatNumber(quote.change)} ({isPositive ? "+" : ""}
            {formatNumber(quote.changePercent)}%)
          </span>
        </div>
        <div className="market-analysis-quote-item">
          <span className="market-analysis-quote-label">Volume</span>
          <span className="market-analysis-quote-value">
            {formatNumber(quote.volume, 0)}
          </span>
        </div>
        <div className="market-analysis-quote-item">
          <span className="market-analysis-quote-label">Market cap</span>
          <span className="market-analysis-quote-value">
            ${formatNumber(quote.marketCap / 1e12, 2)}T
          </span>
        </div>
      </div>
    </div>
  );
};

const AnalysisCard: React.FC<{ analysis: Analysis }> = ({ analysis }) => {
  const recClass =
    analysis.recommendation === "BUY"
      ? "buy"
      : analysis.recommendation === "SELL"
        ? "sell"
        : "hold";
  return (
    <div className="market-analysis-card">
      <div className="market-analysis-card-header">
        <h2>Analysis</h2>
        <p className="market-analysis-timestamp">
          {formatDate(analysis.timestamp, { includeTime: true })}
        </p>
      </div>
      <div className="market-analysis-recommendation-row">
        <span className={`market-analysis-recommendation ${recClass}`}>
          {analysis.recommendation}
        </span>
        <span className="market-analysis-confidence">
          Confidence: {analysis.confidence}%
        </span>
      </div>
      <div className="market-analysis-scores">
        <span>Technical: {analysis.technicalScore}%</span>
        <span>Fundamental: {analysis.fundamentalScore}%</span>
        <span>Sentiment: {analysis.sentimentScore}%</span>
      </div>
      {analysis.reasoning && (
        <div className="market-analysis-reasoning">
          <h3>Reasoning</h3>
          <div
            className="market-analysis-markdown"
            dangerouslySetInnerHTML={{
              __html: analysis.reasoning
                .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
                .replace(/\n/g, "<br />"),
            }}
          />
        </div>
      )}
      {analysis.risks && analysis.risks.length > 0 && (
        <div className="market-analysis-risks">
          <h3>Risks</h3>
          <ul>
            {analysis.risks.map((r, i) => (
              <li key={i}>{r.replace(/\*\*(.*?)\*\*/g, "$1")}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const NewsCard: React.FC<{ news: NewsItem[] }> = ({ news }) => {
  if (!news.length) return null;
  return (
    <div className="market-analysis-card">
      <div className="market-analysis-card-header">
        <h2>Recent News</h2>
      </div>
      <ul className="market-analysis-news-list">
        {news.slice(0, 10).map((item, i) => (
          <li key={i} className="market-analysis-news-item">
            <a
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              className="market-analysis-news-title"
            >
              {item.title}
            </a>
            <p className="market-analysis-news-summary">{item.summary}</p>
            <span className="market-analysis-news-date">
              {formatDate(item.publishedAt)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const HistoricalCard: React.FC<{ historical: HistoricalCandle[] }> = ({
  historical,
}) => {
  if (!historical.length) return null;
  const sorted = [...historical].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );
  const display = sorted.slice(0, 20);
  return (
    <div className="market-analysis-card">
      <div className="market-analysis-card-header">
        <h2>Historical (last {display.length} sessions)</h2>
      </div>
      <div className="market-analysis-table-wrap">
        <table className="market-analysis-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Open</th>
              <th>High</th>
              <th>Low</th>
              <th>Close</th>
              <th>Volume</th>
            </tr>
          </thead>
          <tbody>
            {display.map((row, i) => (
              <tr key={i}>
                <td>{formatDate(row.date)}</td>
                <td>${formatNumber(row.open)}</td>
                <td>${formatNumber(row.high)}</td>
                <td>${formatNumber(row.low)}</td>
                <td>${formatNumber(row.close)}</td>
                <td>{formatNumber(row.volume, 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MarketAnalysisPage;
